import type {
  ColDef,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

import {
  formatCost,
  formatCostExact,
  formatTokens,
  inputTokensTitle,
  outputTokensTitle,
} from '../../utils/formatters';
import defaults from '../../translations/en.json';
import { HISTORY_NUMERIC_CLASS, numberComparator } from './historyColumnDefs';

/** One row's tokens and estimated cost, whatever the grid. */
export type TokenCells = {
  tokens?: number | null;
  input?: number | null;
  cached?: number | null;
  output?: number | null;
  candidates?: number | null;
  thoughts?: number | null;
  questionsCost?: number | null;
  cacheCost?: number | null;
  totalCost?: number | null;
};

/** The last token column: the date filter's frame ends here. */
export const LAST_TOKEN_COLUMN = 'totalCost';

type TokenColumnDefsArgs = {
  /** A row's numbers; undefined for no row. */
  cells: (data: unknown) => TokenCells | undefined;
  /** "See tokens" is on (Mumms admins): the split and cost columns show. */
  showCost?: boolean;
  smallWidget?: boolean;
  /** Extra column props (MAIA Chats' range filter). */
  filter?: ColDef;
};

const costColumn = (
  colId: string,
  headerName: string,
  headerTooltip: string,
  pick: (cells: TokenCells) => number | null | undefined,
  { cells, showCost, filter }: TokenColumnDefsArgs,
): ColDef => ({
  colId,
  headerName,
  headerTooltip,
  width: 100,
  hide: !showCost,
  cellClass: HISTORY_NUMERIC_CLASS,
  comparator: numberComparator,
  valueGetter: ({ data }: ValueGetterParams) => {
    const row = cells(data);
    return row ? pick(row) ?? null : null;
  },
  valueFormatter: ({ value }: ValueFormatterParams) => formatCost(value),
  // the cents a small cost rounds away
  tooltipValueGetter: ({ value }: ITooltipParams) => formatCostExact(value),
  ...filter,
});

/**
 * Tokens (input + output) for everyone. With "See tokens" on (Mumms admins):
 * Total Tokens, Tokens In, Tokens Out, Questions $, Chart cache $, Total $.
 * The hidden columns stay defined, so the question rows keep the same
 * colIds as the conversation rows above them.
 */
export const getTokenColumnDefs = (args: TokenColumnDefsArgs): ColDef[] => {
  const { cells, showCost, smallWidget, filter } = args;
  const tokens = (pick: (row: TokenCells) => number | null | undefined) => ({
    data,
  }: ValueGetterParams) => {
    const row = cells(data);
    return row ? pick(row) ?? null : null;
  };
  return [
    {
      colId: 'tokens',
      headerName: showCost
        ? defaults.historyTotalTokens
        : defaults.historyTokens,
      headerTooltip: defaults.historyTokensTitle,
      width: smallWidget ? 72 : 100,
      cellClass: HISTORY_NUMERIC_CLASS,
      comparator: numberComparator,
      valueGetter: tokens((row) => row.tokens),
      valueFormatter: ({ value }: ValueFormatterParams) => formatTokens(value),
      ...filter,
    },
    {
      colId: 'inputTokens',
      headerName: defaults.historyInputTokens,
      headerTooltip: defaults.historyInputTokensTitle,
      width: 100,
      hide: !showCost,
      cellClass: HISTORY_NUMERIC_CLASS,
      comparator: numberComparator,
      valueGetter: tokens((row) => row.input),
      valueFormatter: ({ value }: ValueFormatterParams) => formatTokens(value),
      tooltipValueGetter: ({ data }: ITooltipParams) =>
        inputTokensTitle(
          defaults.inputTokensTooltip,
          cells(data)?.input,
          cells(data)?.cached,
        ),
      ...filter,
    },
    {
      colId: 'outputTokens',
      headerName: defaults.historyOutputTokens,
      headerTooltip: defaults.historyOutputTokensTitle,
      width: 100,
      hide: !showCost,
      cellClass: HISTORY_NUMERIC_CLASS,
      comparator: numberComparator,
      valueGetter: tokens((row) => row.output),
      valueFormatter: ({ value }: ValueFormatterParams) => formatTokens(value),
      tooltipValueGetter: ({ data }: ITooltipParams) =>
        outputTokensTitle(
          defaults.outputTokensTooltip,
          cells(data)?.output,
          cells(data)?.candidates,
          cells(data)?.thoughts,
        ),
      ...filter,
    },
    costColumn(
      'questionsCost',
      defaults.costQuestions,
      defaults.costQuestionsTitle,
      (row) => row.questionsCost,
      args,
    ),
    costColumn(
      'cacheCost',
      defaults.costCache,
      defaults.costCacheTitle,
      (row) => row.cacheCost,
      args,
    ),
    costColumn(
      LAST_TOKEN_COLUMN,
      defaults.costTotal,
      defaults.costTotalTitle,
      (row) => row.totalCost,
      args,
    ),
  ];
};
